import React, { FC, ReactNode, useState } from 'react';
import {
  Accordion as MuiAccordion,
  AccordionDetails,
  AccordionSummary,
  SxProps,
  Theme,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface Props {
  summary: ReactNode;
  details: ReactNode;
  expanded: boolean;
  onExpandRequest: () => void;
  sx?: SxProps<Theme>;
  summarySx?: SxProps<Theme>;
  detailsStyle?: { [key: string]: string };
  summaryContentStyle?: { [key: string]: string };
  selfManageExpanded?: boolean;
  disabled?: boolean;
  disabledDimsSummary?: boolean;
}

const Accordion: FC<Props> = ({
  summary,
  details,
  expanded,
  onExpandRequest,
  sx,
  summarySx,
  summaryContentStyle,
  detailsStyle,
  selfManageExpanded,
  disabled = false,
  disabledDimsSummary = true,
}) => {
  const theme = useTheme();

  const [internalExpanded, setInternalExpanded] = useState(expanded);

  return (
    <MuiAccordion
      disableGutters
      slotProps={{ heading: { component: 'div' } }}
      expanded={disabled ? false : selfManageExpanded ? internalExpanded : expanded}
      sx={{
        mb: theme.spacing(1),
        borderRadius: `${theme.shape.borderRadius}px`,
        '& .MuiAccordionSummary-root:hover:not(.Mui-disabled)': {
          cursor: 'unset',
        },
        '&:before': {
          content: 'none',
        },
        '& .MuiCollapse-root': {
          ...detailsStyle,
        },
        '& .MuiAccordion-heading': {
          ...summaryContentStyle,
        },
        ...sx,
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon
            onClick={() => {
              if (!disabled) {
                setInternalExpanded((prev) => !prev);
                onExpandRequest();
              }
            }}
            sx={{
              opacity: disabled ? 0.4 : 1,
            }}
          />
        }
        aria-controls="panel1a-content"
        id="panel1a-header"
        sx={{
          py: theme.spacing(2),
          flexDirection: 'row-reverse',
          opacity: disabled && disabledDimsSummary ? 0.4 : 1,
          ...summarySx,
          '& .MuiAccordionSummary-expandIconWrapper': {
            mr: theme.spacing(2),
            width: '1.5em',
            '& :hover': {
              cursor: disabled ? 'default' : 'pointer',
            },
          },
          '& .MuiAccordionSummary-content': {
            justifyContent: 'space-between',
            my: theme.spacing(0),
            '&.Mui-expanded': {
              my: theme.spacing(0),
            },
            ...summaryContentStyle,
          },
        }}
      >
        {summary}
      </AccordionSummary>
      <AccordionDetails>{details}</AccordionDetails>
    </MuiAccordion>
  );
};

export default Accordion;
